import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export interface IBookingDotComBedConfiguration {
  bed_type_id: number;
  bed_count: number;
}

export interface IBookingDotComSubroom {
  type: string;
  bed_configurations: IBookingDotComBedConfiguration[];
}

export enum UnitOfMeasurement {
  SQM = 'SQM',
  SQFT = 'SQFT',
}

export interface IBookingDotComLicenceContentData {
  name: string;
  value: string;
}

export interface IBookingDotComLicence {
  level: string;
  variant_id: string;
  content_data: IBookingDotComLicenceContentData[];
}

export interface IBookingDotComContactDetails {
  name: string;
  email: string;
  language: string;
  phone_type: number;
  phone_number: string;
}

export interface IBookingDotComInvoiceSettings {
  legal_company_name: string;
  contact_name: string;
  country: string;
  address: string;
  city: string;
  zip_code: string;
}

export enum RoomType {
  BedroomSubroom = 'BEDROOM_SUBROOM',
  LivingRoomSubroom = 'LIVING_ROOM_SUBROOM',
  GuestRoom = 'GUEST_ROOM',
}

export const MULTI_ROOM_TYPES: RoomType[] = [
  RoomType.BedroomSubroom,
  RoomType.LivingRoomSubroom,
];
export const SINGLE_ROOM_TYPES: RoomType[] = [RoomType.GuestRoom];

export enum PublicationStep {
  propertySettings = 'property_settings',
  facilities = 'facilities',
  pricingSettings = 'pricing_settings',
  payments = 'payments',
  legalDetails = 'legal_details',
  confirm = 'confirm',
}

export enum PayoutMethod {
  virtualCard = 'VCC',
  bankTransfer = 'BT',
}

export enum PaymentsByBookingStatus {
  accepted = 'ACCEPTED',
  waiting = 'WAITING',
  inProgress = 'IN_PROGRESS',
  successful = 'SUCCESSFUL',
  failed = 'FAILED',
  rejected = 'REJECTED',
}

export interface IPaymentsByBooking {
  status: PaymentsByBookingStatus;
  payout_method: PayoutMethod;
  payout_frequency: string | null;
  rejection_reason: string | null;
  action_required: boolean | null;
}

export default class BookingDotComHotelPublicationModel extends Model {
  @attr('string') step?: PublicationStep;
  @attr('string') status!: string;
  @attr('string') lastCompletedStep!: string;
  @attr('number') size!: number;
  @attr('string') unitOfMeasurement!: UnitOfMeasurement;
  @attr('array', { defaultValue: () => [] }) subrooms!: IBookingDotComSubroom[];
  @attr('boolean') isMultiRoom!: boolean;
  @attr('object-or-null') licence!: IBookingDotComLicence | null;
  @attr('object-or-null') contactDetails!: IBookingDotComContactDetails | null;
  @attr('object-or-null')
  invoiceSettings!: IBookingDotComInvoiceSettings | null;
  @attr('number') maxAdults?: number;
  @attr('number') maxChildren?: number;
  @attr('number') maxInfants?: number;
  @attr('number') cancellationPolicyCode?: number;
  @attr('string') payoutMethod?: PayoutMethod | null;
  @attr('string') payoutFrequency?: string | null;
  @attr('boolean') skipPayments?: boolean;
  @attr('object-or-null') paymentsByBooking!: IPaymentsByBooking | null;
  @hasMany('amenity', { async: false, inverse: null })
  amenities!: ModelRegistry['amenity'][];

  @belongsTo('property', { inverse: null, async: false })
  property!: ModelRegistry['property'];

  @belongsTo('channel-listing', {
    inverse: 'bookingDotComHotelPublication',
    async: false,
  })
  channelListing!: ModelRegistry['channel-listing'];
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'booking-dot-com-hotel-publication': BookingDotComHotelPublicationModel;
  }
}
